import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Coins, Gift, Plus, PencilSimple, Trash, X, Check, ArrowUp, ArrowDown, Wallet, Receipt,
  Clock, CheckCircle, PaperPlaneTilt, ArrowCounterClockwise, EnvelopeSimple,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, Loading, Pill } from "@/components/riq";
import { shortDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useAdminNav } from "@/pages/adminNav";

const ICON_OPTIONS = ["gift", "phone", "wifi", "basket", "gas", "palette", "sparkle", "tag"];
const EMPTY = { title: "", description: "", cost: 100, type: "voucher", icon: "gift", active: true };

export default function AdminRewards() {
  const { user } = useAuth();
  const { openUser } = useAdminNav();
  const [data, setData] = useState(null);
  const [perReceipt, setPerReceipt] = useState(2);
  const [referrerReward, setReferrerReward] = useState(30);
  const [refereeReward, setRefereeReward] = useState(20);
  const [savingRate, setSavingRate] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || "");
  const [sendingTest, setSendingTest] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, ...fields} or null
  const [saving, setSaving] = useState(false);
  const [reds, setReds] = useState(null);
  const [redFilter, setRedFilter] = useState("pending");
  const [fulfilling, setFulfilling] = useState(null);
  const [fulfilModal, setFulfilModal] = useState(null); // {r, note, voucher_type, voucher_code}
  const [rejectModal, setRejectModal] = useState(null); // {r, note}

  const load = () =>
    api.get("/admin/rewards").then((r) => {
      setData(r.data); setPerReceipt(r.data.per_receipt);
      if (r.data.referrer_reward != null) setReferrerReward(r.data.referrer_reward);
      if (r.data.referee_reward != null) setRefereeReward(r.data.referee_reward);
    }).catch(() => setData(false));
  const loadReds = (f = redFilter) => {
    const qs = f === "all" ? "" : `?status=${f}`;
    api.get(`/admin/redemptions${qs}`).then((r) => setReds(r.data)).catch(() => setReds(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadReds(redFilter); }, [redFilter]);

  const setFilter = (f) => { setRedFilter(f); };

  const toggleFulfil = async (r) => {
    // Marking as fulfilled opens a modal to collect voucher details + a note (an email is sent).
    if (r.fulfilment_status !== "fulfilled") {
      setFulfilModal({ r, note: "", voucher_type: r.type || "", voucher_code: "" });
      return;
    }
    // Moving back to pending is a direct action (no email).
    setFulfilling(r.id);
    try {
      await api.put(`/admin/redemptions/${r.id}`, { fulfilment_status: "pending" });
      toast.success("Moved back to pending");
      loadReds(redFilter);
    } catch (e) { toast.error("Could not update"); }
    finally { setFulfilling(null); }
  };

  const confirmReject = async () => {
    const { r, note } = rejectModal;
    setFulfilling(r.id);
    try {
      await api.put(`/admin/redemptions/${r.id}`, { fulfilment_status: "rejected", note: note.trim() });
      toast.success("Claim rejected — the user has been emailed");
      setRejectModal(null);
      loadReds(redFilter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject the claim.");
    } finally { setFulfilling(null); }
  };

  const confirmFulfil = async () => {
    const { r, note, voucher_type, voucher_code } = fulfilModal;
    setFulfilling(r.id);
    try {
      await api.put(`/admin/redemptions/${r.id}`, {
        fulfilment_status: "fulfilled",
        note: note.trim(),
        voucher_type: voucher_type.trim(),
        voucher_code: voucher_code.trim(),
      });
      toast.success("Marked as fulfilled — email sent to the user");
      setFulfilModal(null);
      loadReds(redFilter);
    } catch (e) { toast.error("Could not update"); }
    finally { setFulfilling(null); }
  };

  const saveRate = async () => {
    setSavingRate(true);
    try {
      await api.put("/admin/rewards/config", {
        per_receipt: Number(perReceipt),
        referrer_reward: Number(referrerReward),
        referee_reward: Number(refereeReward),
      });
      toast.success("Earning & referral rewards updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
    finally { setSavingRate(false); }
  };

  const saveReward = async () => {
    if (!editing.title.trim() || !editing.cost) { toast.error("Title and cost are required"); return; }
    setSaving(true);
    const payload = {
      title: editing.title.trim(), description: editing.description || "",
      cost: Number(editing.cost), type: editing.type, icon: editing.icon, active: !!editing.active,
    };
    try {
      if (editing.id) await api.put(`/admin/rewards/${editing.id}`, payload);
      else await api.post("/admin/rewards", payload);
      toast.success(editing.id ? "Reward updated" : "Reward created");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter an email address"); return; }
    setSendingTest(true);
    try {
      const { data: res } = await api.post(`/admin/email/test?email=${encodeURIComponent(testEmail.trim())}`);
      if (res.ok) toast.success(`Test email sent to ${res.to}`);
      else toast.error(res.error || "Send failed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed"); }
    finally { setSendingTest(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try { await api.delete(`/admin/rewards/${r.id}`); toast.success("Reward deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  if (data === null) return <Loading label="Loading rewards…" />;
  if (data === false) return <div className="text-muted-foreground">Could not load rewards administration.</div>;

  const { stats } = data;

  return (
    <div className="space-y-6" data-testid="admin-rewards-section">
      <h2 className="font-head text-2xl font-bold tracking-tight text-riq-primary flex items-center gap-2"><Coins size={24} weight="fill" className="text-riq-gold" /> Rewards &amp; Earnings</h2>

      {/* Earnings overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat icon={ArrowUp} label="Total earned" value={stats.total_earned.toLocaleString()} />
        <MiniStat icon={ArrowDown} label="Total redeemed" value={stats.total_redeemed.toLocaleString()} />
        <MiniStat icon={Wallet} label="Outstanding" value={stats.outstanding.toLocaleString()} />
        <MiniStat icon={Gift} label="Redemptions" value={stats.redemptions_count} />
      </div>

      {/* Earning rate config */}
      <Card>
        <h3 className="font-head font-semibold text-riq-primary mb-1 flex items-center gap-2"><Receipt size={18} weight="duotone" /> Earning rate</h3>
        <p className="text-sm text-muted-foreground mb-4">Moolas awarded for every unique receipt a user scans.</p>
        <div className="flex items-center gap-3">
          <input
            data-testid="admin-per-receipt-input"
            type="number" min="0" value={perReceipt}
            onChange={(e) => setPerReceipt(e.target.value)}
            className="w-28 rounded-xl border border-riq-border px-4 py-2.5 text-sm outline-none focus:border-riq-primary"
          />
          <span className="text-sm text-muted-foreground">Moolas / receipt</span>
          <button
            data-testid="admin-save-earning-btn"
            onClick={saveRate} disabled={savingRate}
            className="ml-auto rounded-full bg-riq-primary text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40 hover:-translate-y-0.5 transition-transform"
          >
            {savingRate ? "Saving…" : "Save rate"}
          </button>
        </div>

        <div className="mt-5 pt-5 border-t border-riq-border">
          <div className="flex items-center justify-between mb-1">
            <h4 className="font-head font-semibold text-riq-primary flex items-center gap-2"><Gift size={17} weight="duotone" /> Referral rewards</h4>
            {data.referral_stats && (
              <span className="text-xs text-muted-foreground">{data.referral_stats.qualified} rewarded · {data.referral_stats.pending} pending</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-4">Paid when a referred user records their first earning receipt.</p>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="admin-referrer-reward-input" type="number" min="0" value={referrerReward}
                onChange={(e) => setReferrerReward(e.target.value)}
                className="w-24 rounded-xl border border-riq-border px-4 py-2.5 text-sm outline-none focus:border-riq-primary" />
              <span className="text-muted-foreground">to the <b>referrer</b></span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="admin-referee-reward-input" type="number" min="0" value={refereeReward}
                onChange={(e) => setRefereeReward(e.target.value)}
                className="w-24 rounded-xl border border-riq-border px-4 py-2.5 text-sm outline-none focus:border-riq-primary" />
              <span className="text-muted-foreground">to the <b>new user</b></span>
            </label>
          </div>
        </div>
      </Card>

      {/* Email delivery */}
      <Card data-testid="admin-email-test">
        <h3 className="font-head font-semibold text-riq-primary mb-1 flex items-center gap-2"><EnvelopeSimple size={18} weight="duotone" /> Email delivery</h3>
        <p className="text-sm text-muted-foreground mb-4">Send a test email to confirm Resend is delivering (works once <b>sliply.co.za</b> is verified).</p>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            data-testid="admin-test-email-input"
            type="email" value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 min-w-[200px] rounded-xl border border-riq-border px-4 py-2.5 text-sm outline-none focus:border-riq-primary"
          />
          <button
            data-testid="admin-send-test-btn"
            onClick={sendTest} disabled={sendingTest}
            className="rounded-full bg-riq-primary text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40 flex items-center gap-2 hover:-translate-y-0.5 transition-transform"
          >
            <PaperPlaneTilt size={16} weight="bold" /> {sendingTest ? "Sending…" : "Send test"}
          </button>
        </div>
      </Card>

      {/* Rewards catalogue */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-semibold text-riq-primary">Rewards catalogue ({data.rewards.length})</h3>
          <button
            data-testid="admin-add-reward-btn"
            onClick={() => setEditing({ ...EMPTY })}
            className="flex items-center gap-2 rounded-full bg-riq-primary text-white px-4 py-2 text-sm font-medium hover:-translate-y-0.5 transition-transform"
          >
            <Plus size={16} weight="bold" /> Add reward
          </button>
        </div>
        <Card className="divide-y divide-riq-border p-0">
          {data.rewards.map((r) => (
            <div key={r.id} data-testid={`admin-reward-row-${r.id}`} className="flex items-center gap-4 px-5 py-4">
              <span className="w-9 h-9 rounded-xl bg-riq-sage flex items-center justify-center shrink-0"><Gift size={18} weight="duotone" className="text-riq-primary" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">{r.title}</span>
                  <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-secondary text-neutral-500">{r.type}</span>
                  {!r.active && <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#FFECEC] text-[#FF6B6B]">hidden</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{r.description}</div>
              </div>
              <span className="inline-flex items-center gap-1 font-head font-bold text-riq-primary shrink-0"><Coins size={14} weight="fill" className="text-riq-gold" />{r.cost}</span>
              <button data-testid={`admin-edit-reward-${r.id}`} onClick={() => setEditing({ ...r })} className="p-2 text-neutral-400 hover:text-riq-primary"><PencilSimple size={18} /></button>
              <button data-testid={`admin-delete-reward-${r.id}`} onClick={() => del(r)} className="p-2 text-neutral-400 hover:text-[#FF6B6B]"><Trash size={18} /></button>
            </div>
          ))}
        </Card>
      </div>

      {/* Redemption fulfilment */}
      <div data-testid="admin-redemptions-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="duotone" /> Redemption fulfilment
            {reds?.pending_count > 0 && (
              <span data-testid="pending-badge" className="rounded-full bg-riq-gold/25 text-[#B25E00] px-2.5 py-0.5 text-xs font-semibold">{reds.pending_count} pending</span>
            )}
          </h3>
          <div className="flex gap-2">
            <Pill testId="red-filter-pending" active={redFilter === "pending"} onClick={() => setFilter("pending")}>Pending{reds?.pending_count != null ? ` (${reds.pending_count})` : ""}</Pill>
            <Pill testId="red-filter-fulfilled" active={redFilter === "fulfilled"} onClick={() => setFilter("fulfilled")}>Fulfilled{reds?.fulfilled_count != null ? ` (${reds.fulfilled_count})` : ""}</Pill>
            <Pill testId="red-filter-all" active={redFilter === "all"} onClick={() => setFilter("all")}>All</Pill>
          </div>
        </div>
        <Card className="p-0">
          {reds === null ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Loading redemptions…</div>
          ) : reds === false ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Could not load redemptions.</div>
          ) : reds.redemptions.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {redFilter === "pending" ? "No pending redemptions — you're all caught up! 🎉" : "No redemptions here yet."}
            </div>
          ) : (
            <div className="divide-y divide-riq-border">
              {reds.redemptions.map((r) => {
                const done = r.fulfilment_status === "fulfilled";
                const rejected = r.fulfilment_status === "rejected";
                return (
                  <div key={r.id} data-testid={`redemption-row-${r.id}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start">
                    <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${done ? "bg-[#E4F7ED]" : rejected ? "bg-red-100" : "bg-riq-gold/20"}`}>
                      {done ? <CheckCircle size={18} weight="fill" className="text-[#22B573]" /> : rejected ? <X size={18} weight="bold" className="text-red-500" /> : <Clock size={18} weight="duotone" className="text-[#B25E00]" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{r.title}</span>
                        <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-secondary text-neutral-500">{r.type}</span>
                        <span data-testid={`redemption-status-${r.id}`} className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${done ? "bg-[#E4F7ED] text-[#22B573]" : rejected ? "bg-red-100 text-red-600" : "bg-riq-gold/25 text-[#B25E00]"}`}>
                          {done ? "Fulfilled" : rejected ? "Rejected" : "Pending"}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        <button data-testid={`redemption-user-link-${r.id}`} onClick={() => r.user_id && openUser(r.user_id)}
                          className="text-riq-primary font-medium hover:underline">{r.user_name || r.user_email || r.user_id}</button>
                        {" · "}{shortDate(r.at)} · {r.cost} Moolas
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        {r.user_email && <span className="break-all">✉ {r.user_email}</span>}
                        {r.phone && <span>📞 {r.phone}</span>}
                        {r.id_number && <span>🪪 {r.id_number}</span>}
                      </div>
                      {done && (r.voucher_code || r.voucher_type || r.fulfilment_note) && (
                        <div data-testid={`redemption-fulfil-detail-${r.id}`} className="text-xs mt-1.5 flex flex-wrap gap-x-3 gap-y-1 items-center">
                          {r.voucher_type && <span className="text-neutral-500">Type: <b className="text-riq-primary">{r.voucher_type}</b></span>}
                          {r.voucher_code && <span className="font-mono bg-[#f2edff] text-riq-primary px-1.5 py-0.5 rounded">{r.voucher_code}</span>}
                          {r.fulfilment_note && <span className="text-neutral-400">“{r.fulfilment_note}”</span>}
                        </div>
                      )}
                      {rejected && r.rejection_note && (
                        <div className="text-xs mt-1.5 text-neutral-400">Reason: “{r.rejection_note}”</div>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0 pl-12 sm:pl-0">
                      {!rejected && (
                        <button
                          data-testid={`fulfil-btn-${r.id}`}
                          onClick={() => toggleFulfil(r)} disabled={fulfilling === r.id}
                          className={`rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-transform hover:-translate-y-0.5 disabled:opacity-40 ${done ? "border border-riq-border text-neutral-500" : "bg-riq-primary text-white"}`}
                        >
                          {done ? <><ArrowCounterClockwise size={13} weight="bold" /> Pending</> : <><PaperPlaneTilt size={13} weight="bold" /> Mark sent</>}
                        </button>
                      )}
                      {!done && !rejected && (
                        <button
                          data-testid={`reject-btn-${r.id}`}
                          onClick={() => setRejectModal({ r, note: "" })} disabled={fulfilling === r.id}
                          className="rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40"
                        >
                          <X size={13} weight="bold" /> Reject
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Fulfilment modal */}
      {fulfilModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6" onClick={() => setFulfilModal(null)}>
          <div data-testid="fulfil-modal" className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-head text-lg font-bold text-riq-primary flex items-center gap-2"><PaperPlaneTilt size={20} weight="duotone" /> Fulfil redemption</h3>
              <button data-testid="fulfil-modal-close" onClick={() => setFulfilModal(null)} className="p-1 text-neutral-400"><X size={20} /></button>
            </div>
            <div className="rounded-xl bg-riq-sage/60 px-4 py-3">
              <div className="font-medium text-sm text-riq-primary">{fulfilModal.r.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {fulfilModal.r.user_name || fulfilModal.r.user_email} · {fulfilModal.r.cost} Moolas
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">The user will get an email confirming their reward, including anything you add below.</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Voucher type">
                <input data-testid="fulfil-voucher-type" value={fulfilModal.voucher_type}
                  onChange={(e) => setFulfilModal({ ...fulfilModal, voucher_type: e.target.value })}
                  className="riq-input" placeholder="e.g. Airtime, Grocery" />
              </Field>
              <Field label="Voucher code">
                <input data-testid="fulfil-voucher-code" value={fulfilModal.voucher_code}
                  onChange={(e) => setFulfilModal({ ...fulfilModal, voucher_code: e.target.value })}
                  className="riq-input font-mono" placeholder="e.g. ABC-123-XYZ" />
              </Field>
            </div>
            <Field label="Message to user (optional)">
              <textarea data-testid="fulfil-note" rows={3} value={fulfilModal.note}
                onChange={(e) => setFulfilModal({ ...fulfilModal, note: e.target.value })}
                className="riq-input resize-none" placeholder="Tracking info, redemption instructions or a personal note…" />
            </Field>
            <div className="flex gap-3 pt-1">
              <button data-testid="fulfil-modal-cancel" onClick={() => setFulfilModal(null)} className="flex-1 rounded-full border border-riq-border py-2.5 text-sm font-medium">Cancel</button>
              <button data-testid="fulfil-modal-confirm" onClick={confirmFulfil} disabled={fulfilling === fulfilModal.r.id}
                className="flex-1 rounded-full bg-riq-primary text-white py-2.5 text-sm font-medium disabled:opacity-40 flex items-center justify-center gap-2">
                <PaperPlaneTilt size={16} weight="bold" /> {fulfilling === fulfilModal.r.id ? "Sending…" : "Mark sent & email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject & refund modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6" onClick={() => setRejectModal(null)}>
          <div data-testid="reject-modal" className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-head text-lg font-bold text-red-600 flex items-center gap-2"><X size={20} weight="bold" /> Reject claim</h3>
              <button data-testid="reject-modal-close" onClick={() => setRejectModal(null)} className="p-1 text-neutral-400"><X size={20} /></button>
            </div>
            <div className="rounded-xl bg-red-50 px-4 py-3">
              <div className="font-medium text-sm text-red-700">{rejectModal.r.title}</div>
              <div className="text-xs text-red-500 mt-0.5">
                {rejectModal.r.user_name || rejectModal.r.user_email} · {rejectModal.r.cost} Moolas
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">The user will get an email explaining the rejection. Their Moolas are <b>not</b> refunded.</p>
            <Field label="Reason (optional, shown to the user)">
              <textarea data-testid="reject-note" rows={3} value={rejectModal.note}
                onChange={(e) => setRejectModal({ ...rejectModal, note: e.target.value })}
                className="riq-input resize-none" placeholder="e.g. Out of stock, invalid details…" />
            </Field>
            <div className="flex gap-3 pt-1">
              <button data-testid="reject-modal-cancel" onClick={() => setRejectModal(null)} className="flex-1 rounded-full border border-riq-border py-2.5 text-sm font-medium">Cancel</button>
              <button data-testid="reject-modal-confirm" onClick={confirmReject} disabled={fulfilling === rejectModal.r.id}
                className="flex-1 rounded-full bg-red-500 text-white py-2.5 text-sm font-medium disabled:opacity-40 flex items-center justify-center gap-2">
                <X size={16} weight="bold" /> {fulfilling === rejectModal.r.id ? "Rejecting…" : "Reject claim"}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Edit / add modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6" onClick={() => setEditing(null)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-head text-lg font-bold text-riq-primary">{editing.id ? "Edit reward" : "New reward"}</h3>
              <button onClick={() => setEditing(null)} className="p-1 text-neutral-400"><X size={20} /></button>
            </div>
            <Field label="Title">
              <input data-testid="reward-form-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="riq-input" placeholder="R50 Grocery Voucher" />
            </Field>
            <Field label="Description">
              <input data-testid="reward-form-desc" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="riq-input" placeholder="Short description" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cost (Moolas)">
                <input data-testid="reward-form-cost" type="number" min="0" value={editing.cost} onChange={(e) => setEditing({ ...editing, cost: e.target.value })} className="riq-input" />
              </Field>
              <Field label="Type">
                <select data-testid="reward-form-type" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="riq-input">
                  <option value="voucher">Voucher</option>
                  <option value="perk">Perk</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <Field label="Icon">
                <select data-testid="reward-form-icon" value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} className="riq-input">
                  {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </Field>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer py-2.5">
                <input data-testid="reward-form-active" type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-riq-primary" />
                Visible to users
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button data-testid="reward-form-cancel" onClick={() => setEditing(null)} className="flex-1 rounded-full border border-riq-border py-2.5 text-sm font-medium">Cancel</button>
              <button data-testid="reward-form-save" onClick={saveReward} disabled={saving} className="flex-1 rounded-full bg-riq-primary text-white py-2.5 text-sm font-medium disabled:opacity-40 flex items-center justify-center gap-2">
                <Check size={16} weight="bold" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <Card className="p-4">
      <Icon size={16} weight="duotone" className="text-riq-primary mb-2" />
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-head text-xl font-bold text-riq-primary mt-0.5">{value}</div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      {children}
    </div>
  );
}
